@import 'fluid-table.theme.less';

@import '../../styles/mixins/fluid-icon.mixin.less';
@import '../../styles/mixins/fluid-fading-animations.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: block;

  *,
  ::after,
  ::before {
    box-sizing: border-box;
  }

  .fluid-table-wrapper {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @table-tokens: @table-tokens-legacy;
      @table();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @table-tokens: @table-tokens-lm;
      @table();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp,
    &.default {
      @import '../../styles/variables-lm-corp.less';
      @table-tokens: @table-tokens-corp;
      @table();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @table-tokens: @table-tokens-portal;
      @table();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@table: {
  @header-row: @table-tokens[@header-row];
  @header-colgroup: @table-tokens[@header-colgroup-cell];
  @header-cell: @table-tokens[@header-cell];
  @filter: @header-cell[@filter-icon];
  @sort: @header-cell[@sort-icon];
  @row: @table-tokens[@row];
  @cell: @table-tokens[@cell];
  @footer-row: @table-tokens[@footer-row];

  // ==== Utility Variables

  @header-cell-line-height: 1.125;
  @data-cell-line-height: 1.375;
  @interactive-cell-size: 3em;

  // ---------------- //

  position: relative;

  &:not(.inline) {
    margin-bottom: @margin-vertical;
  }

  // ==== Horizontal Scroll Shadows

  .overflow-shadow-left,
  .overflow-shadow-right {
    isolation: isolate;
    contain: strict;
    width: 0.5em;
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;

    &.shadow-inert {
      display: none;
    }

    &.shadow-hidden {
      .fade-out(@duration: 150ms);
    }

    &.shadow-visible {
      .fade-in(@duration: 100ms);
    }
  }

  .overflow-shadow-left {
    left: 0;
    background: radial-gradient(farthest-side at 0% 50%, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0));
  }

  .overflow-shadow-right {
    right: 0;
    background: radial-gradient(farthest-side at 100% 50%, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0));
  }

  // ==== Table Wrapper

  .table-wrapper {
    position: relative;

    // Prevent accidental "horizontal swipe" browser navigation
    // Details: https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior-x
    &.allow-overflow-x {
      overscroll-behavior-x: contain;
    }

    &.loading {
      min-height: 20em;
    }

    &:not(.allow-overflow-x) {
      &::-webkit-scrollbar {
        display: none; // Chrome, Safari, and Opera
      }

      -ms-overflow-style: none; // Internet Explorer, Edge
      scrollbar-width: none; // Firefox
    }

    &.loading {
      min-height: 20em;
    }

    // == Loading w/ Max-Height Set

    &.prevent-scroll {
      overflow-y: clip !important;
    }

    &.with-max-height {
      overflow-y: scroll;
      overscroll-behavior-y: contain;
      border-bottom: 1px solid @header-row[background-color];
      position: relative;
      margin-bottom: 1em;

      &.sticky-headers {
        thead {
          position: sticky;
          top: 0;
          z-index: 3;

          th {
            &.with-split-points:not(.table-header-colgroup-cell) {
              &::before {
                width: @header-cell[border-width];
                position: absolute;
                top: 0;
                bottom: 0;
                right: -@header-cell[border-width];
                background-color: @header-cell[border-color];
                content: "";
                z-index: 1;
              }
            }

            &::after {
              height: @header-cell[border-width];
              position: absolute;
              bottom: -@header-cell[border-width];
              left: 0;
              right: 0;
              background-color: @header-cell[border-color];
              content: "";
              z-index: 1;
            }
          }
        }
      }
    }

    // == Sticky Column

    &.sticky-column {
      overflow-x: auto;

      thead {
        position: relative;
        top: 0;
        z-index: 3;
      }

      th.sticky-column,
      td.sticky-column {
        position: sticky;
        left: 0;
        z-index: 2;
      }

      th.sticky-column {
        background-color: @header-cell[background-color];
      }

      td.sticky-column {
        background-color: #f9f9f9;
      }
    }

    // == Empty State

    .table-empty-state {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      width: 100%;
      height: calc(100% - @header-row[height]);
      min-height: 200px;
    }
  }

  // ==== Header (Slot)

  .fluid-table-header {
    background-color: @header-row[background-color];
    color: @header-row[color];
    border-bottom: @header-row[border-bottom];
    font-size: @header-row[font-size];
    font-family: @font-family;

    .cell-padding(@header-row);

    &.visible {
      display: block;
    }

    &.hidden {
      display: none;
    }
  }

  // ==== Table Element

  .fluid-table {
    width: 100%;
    max-width: 100%;
    margin-bottom: 1rem;
    background-color: transparent;
    border-collapse: collapse;
    font-size: @table-tokens[font-size];
    color: @table-tokens[text-color];
    font-family: @font-family;

    &:not(.allow-overflow-x) {
      overflow-x: hidden;
    }

    &.allow-overflow-x {
      overflow-x: auto;
    
      th:not(.fixed-width),
      td {
        white-space: nowrap;
    
        fluid-popover {
          white-space: normal;
        }
      }
    }

    // == Header Cell / Data Cell Shared Styles

    th,
    td {
      background-clip: padding-box; // Fixes Firefox "invisible borders"

      &.with-split-points {
        vertical-align: middle;
      }

      &.hidden {
        display: none
      }

      &.left {
        text-align: left;
      }

      &.right {
        text-align: right;
      }

      &.center {
        text-align: center;
      }
    }


    // -------------------------------- //
    // -- TABLE HEADER CELLS

    .table-header-colgroup-cell,
    .table-header-cell {
      position: relative;
      vertical-align: @header-cell[vertical-align];
      background-color: @header-cell[background-color];
      color: @header-cell[color];
      text-align: left;
      font-weight: @header-cell[font-weight];
      line-height: @header-cell-line-height;
      overflow-wrap: break-word;
      hyphens: auto;

      .cell-padding(@cell);

      // Content Alignment

      &.left {
        text-align: left;
      }

      &.center {
        text-align: center;
      }

      &.right {
        text-align: right;
      }

      // Split-Point Header

      &.with-split-points {
        vertical-align: middle;

        .header-cell-content {
          align-items: center;
        }
      }
    }

    // == Header Column Group (Parent)

    .table-header-colgroup-cell {
      background-color: @header-colgroup[background-color];

      @header-cell-pseudo-border();

      // Override default pseudo-border color
      &::before {
        background-color: @header-cell[border-color];
      }
    }

    // == Header Cell (Parent)

    .table-header-cell {

      &:not(.with-split-points) {
        @header-cell-pseudo-border();
      }

      &.with-split-points {
        border-right-style: @border-style;
        border-right-color: @header-cell[border-color];
        border-right-width: @header-cell[border-width];

        // Split Point Top-Level

        &:not([rowspan="2"]) {
          font-weight: @font-weight-bold;
        }

        // Split Point Bottom-Level

        &.last-split {
          font-weight: @font-weight-medium;
        }
      }

      &.clickable {
        cursor: pointer;
      }

      &.editable-column {
        min-width: 8em;
      }

      &.action-column {
        text-align: center;
      }

      // == Column Visibility Menu Cell

      &.column-visibility {
        padding-top: @cell[padding-top] / 1.5;
        padding-bottom: @cell[padding-bottom] / 1.5;
        padding-left: @cell[padding-left] / 2;
        padding-right: @cell[padding-right] / 2;

        // Display "trigger" Button "active state" when header CELL has :hover
        &:hover {
          .column-visibility-trigger {
            &::part(button) {
              color: @filter[hover-fill-color]; // Sets Icon "fill" color
              background-color: @filter[hover-background-color];
            }
          }
        }

        // == Column Visibility Context Menu

        .column-visibility-menu {
          display: inline-flex;
          justify-content: center;
          align-items: center;

          // Display "trigger" Button "active state" when Context Menu is open
          &[data-menu-open] {
            .column-visibility-trigger {
              &::part(button) {
                color: @filter[hover-fill-color]; // Sets Icon "fill" color
                background-color: @filter[hover-background-color];
              }
            }
          }
        }

        // == Column Visibility Trigger Button

        .column-visibility-trigger {
          color: @header-cell[color];

          // Override default Button :focus-visible styles for consistency with other Table controls
          &::part(button) {
            &:focus-visible {
              color: @filter[hover-fill-color]; // Sets Icon "fill" color
              background-color: @filter[hover-background-color];
            }
          }
        }
      }

      &.toggle-column {
        min-width: @interactive-cell-size;
        text-align: center;

        &.disabled {
          cursor: not-allowed;
        }
      }

      &.expand-trigger-column {
        text-align: center;
        cursor: pointer;
        outline: none;

        &:focus-visible {
          .header-icon {
            @button-cell-focus-state();
          }
        }

        .header-icon {
          vertical-align: middle;
          border-radius: 0.25px; // Softens "focus outline"
        }
      }
    }

    // == Selectable Rows Header Cell Label (Optional)

    .toggle-column-label {
      display: block;
      font-size: inherit;
      line-height: @header-cell-line-height;

      & ~ .header-cell-toggle {
        margin-top: 0.25em;
      }

      .toggle-column-label-text {
        vertical-align: middle;
      }
    }

    // == Selectable Rows "Toggle All" Toggle

    .header-cell-toggle {
      display: flex;
      justify-content: center;
      position: relative;
      pointer-events: none; // Disable click events (handled by parent <th>)

      --I-label-line-height: @header-cell-line-height;
    }

    // == Header Cell Action Container (Direct Child)

    .header-cell-action {
      width: 100%;
      position: relative;
      line-height: @header-cell-line-height;

      &.icon-only {
        line-height: 0.8;
      }
    }

    // == Header Cell Content Container (Direct Child)

    .header-cell-content {
      width: 100%;
      display: flex;
      align-items: @header-cell[@content-alignment]; // NOT split point (see above)
      justify-content: space-between;
      position: relative;

      &.align-left {

        .sort-icon {
          margin-left: 0.5em;
          margin-right: auto;
        }
      }

      &.align-center {
        justify-content: center;

        .sort-icon {
          order: -1;
          margin-right: 0.5em;
        }
      }

      &.align-right {
        justify-content: flex-end;

        .sort-icon {
          order: -1;
          margin-right: 0.5em;
        }
      }
    }

    // == Header Cell Label

    .header-cell-label {
      flex: 0 1 auto;
      display: block;
      font-size: inherit;
      line-height: @header-cell-line-height;

      .info-popover {
        margin-left: 0.3em;
        text-align: left;
      }

      .info-popover-icon {
        color: @header-cell[color]; // Sets FLUID Icon "fill" color
      }
    }

    // == Header Cell Label Text

    .header-cell-label-text {
      vertical-align: middle;
    }

    // == Header Cell Filter Menu (Context Menu)

    .header-cell-filter-menu {
      height: @filter[height];
      display: flex;
      margin-left: 0.5em;
      position: relative;

      &:hover,
      &:focus-within,
      &[data-menu-open] {
        .filter-icon {
          color: @filter[hover-fill-color]; // Sets Icon "fill" color

          &::before {
            background-color: @filter[hover-background-color];
          }
        }
      }
    }

    // == Header Cell Filter Icon

    .filter-icon {
      transform-style: preserve-3d;

      &::before {
        display: block;
        position: absolute;
        top: -0.5em;
        bottom: -0.5em;
        left: -0.5em;
        right: -0.5em;
        transform: translateZ(-1px);
        background-color: transparent;
        border-radius: @border-radius;
        content: "";

        .transition(@property: background-color; @duration: short);
      }
    }

    // == Header Cell Sort Icon

    .sort-icon {
      height: @sort[height];
      display: inline-flex;
      color: @header-cell[color]; // Sets FLUID Icon "fill" color
    }

    // == Header Icon (Generic)

    .header-icon {
      color: @header-cell[color];

      .transition(@property: color; @duration: short);
    }

    // == Disable User Interaction

    .non-interactive {
      cursor: default;
      pointer-events: none;
      user-select: none;
    }


    // -------------------------------- //
    // -- TABLE HEADER ROWS

    .table-header-colgroup-row,
    .table-header-row {
      border-bottom-style: @border-style;
      border-bottom-color: @header-cell[border-color];
      border-bottom-width: @header-cell[border-width];

      th {
        &:last-of-type:not(.last-split) {
          border-right-width: 0;

          &::before {
            width: 0;
          }
        }
      }
    }

    // == Header Column Group Row

    .table-header-colgroup-row {}

    // == Header Row

    .table-header-row {

      &.no-action-column {
        th.last-split:last-of-type {
          border-right-width: 0;
        }
      }
    }


    // -------------------------------- //
    // -- TABLE BODY

    tbody {

      // == Loading State

      &.loading {
        position: relative;
        pointer-events: none;
        user-select: none;

        &::after {
          display: block;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: rgba(@white, 0.75);
          content: "";
        }
      }
    }


    // -------------------------------- //
    // -- TABLE ROW

    tr {
      background-color: @white;

      &.highlight {
        background-color: @cell[highlight];
      }

      &.row-disabled {
        cursor: not-allowed;
        position: relative;

        // TODO: USE "disabled-overlay" SCSS MIXIN
        &::after {
          position: absolute;
          inset: 0;
          background-color: white;
          opacity: 0.2;
          content: '';
          pointer-events: none;
        }
      }

      &.hidden {
        display: none;
      }

      &.fluid-table-additional-header-row {
        background-color: @color-tertiary;
        color: @white;

        td {
          border-right: none;

          &.sortable {
            &:hover {
              cursor: pointer;
            }
          }
        }
      }

      &.fluid-table-group-header {
        background-color: #efefef;

        .table-data-cell {
          &.sortable {
            cursor: pointer;
          }

          .group-sort-icon {
            display: inline-block;
            margin-left: 5px;
          }
        }
      }

      &.fluid-table-footer {
        border-top: @footer-row[border-top];
        border-top: none;
        background-color: @row[striped-background];

        td {
          border-right: none;
        }
      }

      &.fluid-table-group-footer {
        border-top: @footer-row[border-top];
      }

      &.additional-header {
        &.striped-bg {
          &:not(.fluid-table-group-header) {
            background-color: @row[striped-background];

            &.highlight {
              background-color: @cell[highlight];
            }
          }
        }
      }

      // == Subset "Header Row" (i.e. parent of subset rows)

      &.subset-header {

        &.subset-rows-visible {
          font-weight: @font-weight-bold;
          background-color: @liberty-gray-10;
        }
      }

      // == Subset Row

      &.subset-row {

        &.expanded {
          background-color: #f9f9f9;
        }

        &.subset-last,
        &.row-border.subset-last {
          border-bottom-width: @row[border-bottom-width] * 2;
          border-bottom-color: darken(@row[border-bottom-color], 20%);
        }
      }

      // == Expandable Row

      &.expand-row {

        &:not(.expanded) {
          display: none;
        }
      }

      // == Expandable Form

      &.expand-form {

        .form {
          background-color: white;
          border: 1px solid lightgrey;
        }

        &:not(.expanded) {
          display: none;
        }
      }

      &.filtered {
        display: none;
      }
    }


    // -------------------------------- //
    // -- TABLE BODY ROW

    tbody tr {

      // == Striped Table

      &.striped-bg {
        &:not(.fluid-table-group-header) {
          &:not(.grouped) {
            background-color: @row[striped-background];

            &.highlight {
              background-color: @cell[highlight];
            }
          }
        }
      }

      // == Row Borders

      &.row-border {
        &:not(.fluid-table-group-header) {
          border-bottom-width: @row[border-bottom-width];
          border-bottom-color: @row[border-bottom-color];
          border-bottom-style: @border-style;
        }

        .table-data-cell:not(:last-of-type) {
          border-right: @cell[@row-borders-cell-border];
        }
      }

      // == Hover State

      &:hover {
        &:not(.fluid-table-group-header) {
          &:not(.fluid-table-additional-header-row) {
            &:not(.grouped) {
              background-color: @row[hover-background];

              &.highlight {
                background-color: @row[hover-background];
              }
            }
          }
        }
      }
    }


    // -------------------------------- //
    // -- TABLE DATA CELLS

    // TODO: Migrate/remove all "td" element selector styles to ".table-data-cell"

    td {
      line-height: @data-cell-line-height;
      border-right: @cell[border-right];

      .cell-padding(@cell);

      &:last-child {
        border-right-width: 0;
      }

      &.highlight {
        background-color: @cell[highlight];
      }
    }

    // == Table Data Cell

    .table-data-cell {
      line-height: @data-cell-line-height;
      border-right: @cell[border-right];

      .cell-padding(@cell);

      &.action-cell,
      &.toggle-cell,
      &.expand-trigger-cell {
        vertical-align: middle;
        text-align: center;
        padding: 0;
      }

      .editable-cell {
        &.left,
        &.center,
        &.right {
          display: flex;
        }

        &.left {
          justify-content: left;
        }

        &.center {
          justify-content: center;
        }

        &.right {
          justify-content: right;
        }
      }

      // Selectable Row Toggle Cell

      &.toggle-cell {
        min-width: @interactive-cell-size;
        cursor: pointer;

        &:not(.disabled) {

          &:hover,
          &:focus-visible {
            .data-cell-toggle-container:not(.toggle-switch) {
              background-color: @liberty-gray-4;
            }
          }
        }

        &.disabled {
          cursor: not-allowed;
        }
      }

      // Expandable Row Trigger Cell

      &.expand-trigger-cell {
        min-width: @interactive-cell-size;
        background-color: transparent;
        outline: none;
        cursor: pointer;

        .transition(@property: background-color; @duration: short);

        &:hover,
        &:focus-visible {
          background-color: @liberty-gray-4;
        }

        &:focus-visible {
          fluid-icon {
            @button-cell-focus-state();
          }
        }

        fluid-icon {
          vertical-align: middle;
          border-radius: 0.25px; // Softens "focus outline"
          color: inherit; // Sets FLUID Icon "fill" color
        }
      }
    }

    // == Selectable Row Toggle Container (Direct Child)

    .data-cell-toggle-container {
      margin: 0 auto;

      &:not(.toggle-switch) {
        @interactive-cell-foundation();
      }
    }

    // == Selectable Row FLUID Toggle

    .data-cell-toggle {
      pointer-events: none; // Disable click events (handled by parent <td>)

      --I-label-line-height: @data-cell-line-height;
    }

    // == Selectable Row Radio Button

    .data-cell-toggle-input {
      margin: 0;
      outline-offset: 4px;
      cursor: pointer;
      pointer-events: none; // Disable click events (handled by parent <td>)

      &.disabled {
        cursor: not-allowed;
      }
    }

    // == Truncated Cell Popover

    .popover-tooltip-trigger {
      display: inline-block;
      color: @table-tokens[text-color];
      line-height: @data-cell-line-height;

      &.align-left {
        text-align: left;
      }

      &.align-right {
        text-align: right;
      }

      &.align-center {
        text-align: center;
      }
    }

    .popover-tooltip-content {
      white-space: normal;
    }

    // == Fixed-Width Cell (columnAdjustments)

    .restricted-size {
      display: inline-block;
      white-space: break-spaces;
      vertical-align: middle;
    }
  }

  .overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0;
    cursor: progress;
    z-index: 1;
    margin-top: 3.5em;
  }
}

// ================================================================ //
// -- Utility Detached Ruleset(s)

/**
 * Stylized borders between <th> cells ("::before" pseudo-element)
 */
@header-cell-pseudo-border: {
  @pseudo-border: @header-cell[@pseudo-border];

  &::before {
    width: @pseudo-border[border-width];
    height: @pseudo-border[height];
    max-height: @pseudo-border[max-height];
    position: absolute;
    top: @pseudo-border[top];
    bottom: @pseudo-border[bottom];
    right: -(@pseudo-border[border-width] / 2);
    background-color: @pseudo-border[border-color];
    content: "";
    z-index: 1;
  }
}

/**
 * Base styles for interactive <td> cells (e.g. Toggle cell)
 */
@interactive-cell-foundation: {
  width: @interactive-cell-size - 0.5em;
  height: @interactive-cell-size - 0.5em;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: @interactive-cell-size * 2;

  .transition(@property: background-color; @duration: short);
}

/**
 * Focus state for child element within <th>/<td> cells with
 * "role: button". Displays smaller :focus "outline" around
 * child element (instead of displaying "outline" around entire
 * <th>/<td> element).
 */
@button-cell-focus-state: {
  outline-style: solid;
  outline-width: 2px;
  outline-offset: 4px;
  outline-color: Highlight;

  @media (-webkit-min-device-pixel-ratio: 0) {
    outline-color: -webkit-focus-ring-color;
  }
}


// ================================================================ //
// -- Mixins

.cell-padding(@cell-type) {
  padding-top: @cell-type[padding-top];
  padding-bottom: @cell-type[padding-bottom];
  padding-left: @cell-type[padding-left];
  padding-right: @cell-type[padding-right];
}
